import {produce} from 'immer';
import {isEqual} from 'lodash';
import {useMemo, useState} from 'react';
import styled from 'styled-components';
import {Button, ButtonGroup} from '@/components/Button';
import {Modal} from '@/components/Modal';
import {KodataCategory} from '@/enums';
import {KodataStoresQueryFilters} from '@/map/enums';
import {convertToMyfcCategoryFromKodata, toStringFromRange} from '@/map/utils';
import {Divider, FlexFullGrow, IconClose, PaddingBox, Section} from '@/styled-components';
import {FilterChip, FilterChips} from '../StyledComponents';
import {AvgSalesFilter} from './AvgSalesFilter';
import {CategoryFilter} from './CategoryFilter';

interface IProps {
  area?: string;
  currentFilters?: Array<{key: KodataStoresQueryFilters; value: any}>;
  onSearch?(filters: Array<{key: KodataStoresQueryFilters; value: any}>): void;
  onClose?(): void;
}

const omitEmptyFilter = (
  filters: Array<{key: KodataStoresQueryFilters; value: any}>
) =>
  filters &&
  filters.filter(
    (i) =>
      i.value &&
      (Array.isArray(i.value)
        ? i.value.length
        : typeof i.value === 'string'
        ? i.value.trim().length
        : i.value)
  );

const CATEGORY_LIST = Object.values(KodataCategory);

export const FilterModal = ({
  area,
  currentFilters,
  onSearch,
  onClose,
}: IProps) => {
  const searchFilter = useMemo(
    () => (currentFilters ? omitEmptyFilter(currentFilters) : []),
    [currentFilters]
  );

  const [filters, setFilters] = useState<
    Array<{key: KodataStoresQueryFilters; value: any}>
  >([...searchFilter]);

  const handleMultiButtonChange = (
    key: KodataStoresQueryFilters,
    value: any
  ) => {
    setFilters(
      produce((draft) => {
        const idx = draft.findIndex((i) => i.key === key);

        if (idx >= 0) {
          const prev = draft[idx];

          if (!prev.value || !Array.isArray(prev.value)) {
            draft[idx] = {key, value: [value]};
          }

          const array = [...prev.value];
          const subIdx = array.findIndex((v) => isEqual(v, value));

          if (subIdx >= 0) {
            array.splice(subIdx, 1);
          } else {
            array.push(value);
          }

          if (!array.length) {
            draft.splice(idx, 1);
          } else {
            draft[idx] = {key, value: array};
          }
        } else {
          draft.push({key, value: [value]});
        }
      })
    );
  };

  const clearFilter = (key?: KodataStoresQueryFilters, value?: any) => {
    if (key) {
      if (value) {
        handleMultiButtonChange(key, value);
      } else {
        setFilters(
          produce((draft) => {
            const idx = draft.findIndex((i) => i.key === key);

            if (idx >= 0) {
              draft.splice(idx, 1);
            }
          })
        );
      }
    } else {
      setFilters([]);
    }
  };

  const handleSearch = () => {
    onSearch?.(filters);
    onClose?.();
  };

  return (
    <Modal
      bodyOverflow="unset"
      title="매장 찾기 필터"
      width={360}
      onClose={onClose}
    >
      <Root>
        <Body>
          <PaddingBox top={24} left={16} right={16}>
            <Section spacing={4}>
              <CategoryFilter
                categories={CATEGORY_LIST}
                initialValues={
                  filters.find(
                    (x) => x.key === KodataStoresQueryFilters.categories
                  )?.value
                }
                onChange={handleMultiButtonChange}
              />
            </Section>
            <Section spacing={4}>
              <AvgSalesFilter
                initialValues={
                  filters.find(
                    (x) => x.key === KodataStoresQueryFilters.salesRanges
                  )?.value
                }
                onChange={handleMultiButtonChange}
              />
            </Section>
            <Section spacing={4}>
              <Divider />
            </Section>
          </PaddingBox>
        </Body>
        <Footer>
          {(!!area?.length || !!filters?.length) && (
            <SelectedFilterBox>
              <PaddingBox top={16} left={16} right={16}>
                <FilterChips>
                  {!!area?.length && <FilterChip>{area}</FilterChip>}
                  {filters.map(
                    (i) =>
                      i.key !== KodataStoresQueryFilters.nameRegex &&
                      (i.key === KodataStoresQueryFilters.salesRanges
                        ? (Array.isArray(i.value[0]) ? i.value : [i.value]).map(
                            (v: any, idx: number) => (
                              <FilterChip key={i.key + idx}>
                                {toStringFromRange(v, '원')}
                                <IconClose
                                  onClick={() => clearFilter(i.key, v)}
                                />
                              </FilterChip>
                            )
                          )
                        : Array.isArray(i.value) &&
                          i.value.map((x, idx) => (
                            <FilterChip key={i.key + idx}>
                              {convertToMyfcCategoryFromKodata(x)}
                              <IconClose
                                onClick={() => clearFilter(i.key, x)}
                              />
                            </FilterChip>
                          )))
                  )}
                </FilterChips>
              </PaddingBox>
            </SelectedFilterBox>
          )}
          <PaddingBox top={16} left={16} right={16} bottom={24}>
            <ButtonGroup>
              <Button
                size="regular"
                variant="outline"
                color="black"
                onClick={() => clearFilter()}
              >
                초기화
              </Button>
              <FlexFullGrow />
              <Button
                size="regular"
                variant="fill"
                color="primary"
                onClick={handleSearch}
              >
                적용
              </Button>
            </ButtonGroup>
          </PaddingBox>
        </Footer>
      </Root>
    </Modal>
  );
};

const Root = styled.div`
  max-height: 640px;
  height: 80%;
  display: flex;
  flex-direction: column;
`;

const Body = styled.div`
  overflow: auto;
  flex-grow: 1;
`;

const Footer = styled.div``;

const SelectedFilterBox = styled.div`
  overflow: auto;
  max-height: 72px;
`;
